import React, { useEffect, useMemo, useState } from 'react';
import {
  ExtapiInvoice,
  ExtapiInvoiceSearchQueryParams,
  ExtapiInvoiceSearchResult,
  RmaInvoice,
} from '../../../Types/MyPages/InvoicesModel';
import MyPagesInvoiceFilterForm from '../Invoices/MyPagesInvoiceFilterForm';
import { getInvoiceHeaders } from '../../../Actions/Invoices.action';
import MyPagesInvoiceList from '../Invoices/MyPagesInvoiceList';
import Icon from '../../Icon';
import { translate } from '../../../Services/translation';
import clsx from 'clsx';
import MyPagesRmaRowsModal from './MyPagesRmaRowsModal';

interface MyPagesRmaListProps {
  onInvoiceSaved: (invoice: RmaInvoice) => void;
  currentInvoices: RmaInvoice[];
}

const MyPagesRmaList = ({
  onInvoiceSaved,
  currentInvoices,
}: MyPagesRmaListProps) => {
  const [selectedInvoice, setSelectedInvoice] = useState<
    RmaInvoice | undefined
  >();
  const [invoices, setInvoices] = useState<
    ExtapiInvoiceSearchResult | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<
    ExtapiInvoiceSearchQueryParams | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const fetchInvoices = async (
    filter?: ExtapiInvoiceSearchQueryParams
  ) => {
    setIsLoading(true);
    setErrorMessage(undefined);
    try {
      const data = await getInvoiceHeaders(filter);
      const filteredData = data.filter(
        (x) => x.documentType != 'Credit Memo'
      );

      const result = {
        hits: filteredData.length,
        result: filteredData,
      } as ExtapiInvoiceSearchResult;

      setInvoices(result);
    } catch (error) {
      if (error.response?.status == 404) {
        setErrorMessage(translate('mypage.invoices.filter-no-result'));
      } else {
        setErrorMessage(translate('general.error'));
      }
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(queryParams);
  }, []);

  const totalReturnQuantity = useMemo(
    () =>
      currentInvoices
        .flatMap((x) => x.rows)
        .reduce((prev, row) => prev + row.returnQuantity, 0),
    [currentInvoices]
  );

  return (
    <>
      <MyPagesInvoiceFilterForm
        filter={queryParams}
        onFilterChange={setQueryParams}
        isLoading={isLoading}
        onSearch={() => fetchInvoices(queryParams)}
      />
      {!isLoading ? (
        <>
          {!errorMessage && (
            <MyPagesInvoiceList
              showDownload={false}
              isLoading={isLoading}
              hasError={false} // TODO
              invoices={invoices?.result}
              onInvoiceClick={(invoice) =>
                setSelectedInvoice(
                  currentInvoices.find(
                    (x) => x.documentNo == invoice.documentNo
                  ) ?? (invoice as RmaInvoice)
                )
              }
              invoiceButtonText={translate('mypage.rma.create-rma')}
              invoiceClassName={(invoice: ExtapiInvoice) =>
                clsx(
                  !!currentInvoices.find(
                    (x) => x.documentNo == invoice.documentNo
                  ) && '!bg-warning-50'
                )
              }
            />
          )}
          {errorMessage && (
            <small className="text-error-500">{errorMessage}</small>
          )}
          <div className="-mt-2 flex items-center justify-end">
            <div className="text-md">
              <strong>{totalReturnQuantity}</strong>{' '}
              {translate('mypage.rma.selectedarticles').toLowerCase()}.{' '}
              <strong>{currentInvoices.length}</strong>{' '}
              {translate('mypage.rma.invoices').toLowerCase()}
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-[480px] w-full animate-pulse items-center justify-center rounded bg-gray-100">
          <Icon name="refresh" className="h-10 w-10 animate-spin" />
        </div>
      )}
      {selectedInvoice && (
        <MyPagesRmaRowsModal
          invoice={selectedInvoice}
          onComplete={onInvoiceSaved}
          onClose={() => setSelectedInvoice(undefined)}
        />
      )}
    </>
  );
};

export default MyPagesRmaList;
